import * as React from 'react';
import {
    Alert,
    Box,
    Chip,
    Drawer,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import { Close, Send, SmartToyOutlined, RestartAlt } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AI_SERVICE } from '../../services/ai/ai-service';
import { useAuth } from '../../services/common/auth-context';
import AIChatMessage, { DisplayMessage } from './ai-chat-message';
import { ChatTurnDto } from '../../helper-types/dtos/ai/requests/send-chat-message-request-dto';
import { AvailableToolResponseDto } from '../../helper-types/dtos/ai/responses/available-tool-response-dto';
import { LanguageCode } from '../../helper-types/general/consts/language-code';

const CAPABILITY_CHIP_MAX_CHARS = 42;

const truncateForChip = (text: string): string =>
    text.length > CAPABILITY_CHIP_MAX_CHARS ? `${text.slice(0, CAPABILITY_CHIP_MAX_CHARS).trimEnd()}…` : text;

interface AIChatPanelProps {
    open: boolean;
    onClose: () => void;
}

const PANEL_WIDTH = { xs: '100%', sm: 420, md: 480 };

// Animations — same kit as add-questions-bank-dialog / ai-generate-questions-dialog so the
// chat drawer feels like part of the same design language.
const slideIn = keyframes`
    from { opacity: 0; transform: translateX(20px); }
    to { opacity: 1; transform: translateX(0); }
`;

const pulse = keyframes`
    0%, 100% { opacity: 0.4; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.05); }
`;

const shimmer = keyframes`
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
`;

const StyledDrawerPaper = styled(Box)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.vars.palette.background.paper,
    borderLeft: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
    borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
    boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 40px ${alpha(theme.palette.primary.main, 0.08)}`,
    animation: `${slideIn} 0.3s ease-out`,
    ...theme.applyStyles('dark', {
        borderLeft: `1px solid ${alpha(theme.palette.primary.light, 0.3)}`,
        borderRight: `1px solid ${alpha(theme.palette.primary.light, 0.3)}`,
        boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 40px ${alpha(theme.palette.primary.main, 0.15)}`,
    }),
}));

const PanelHeader = styled(Box)(({ theme }) => ({
    position: 'relative',
    padding: theme.spacing(2.5),
    backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.dark, 0.05)} 100%)`,
    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    overflow: 'hidden',
    flexShrink: 0,
    ...theme.applyStyles('dark', {
        backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.dark, 0.08)} 100%)`,
        borderBottom: `1px solid ${alpha(theme.palette.primary.light, 0.2)}`,
    }),
}));

const GlowOrb = styled(Box)(({ theme }) => ({
    position: 'absolute',
    width: 90,
    height: 90,
    right: '5%',
    top: '15%',
    borderRadius: '50%',
    background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.2)} 0%, transparent 70%)`,
    animation: `${pulse} 3s ease-in-out infinite`,
    pointerEvents: 'none',
    ...theme.applyStyles('dark', {
        background: `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.25)} 0%, transparent 70%)`,
    }),
}));

const IconWrapper = styled(Box)(({ theme }) => ({
    width: 44,
    height: 44,
    borderRadius: theme.spacing(1.5),
    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
    flexShrink: 0,
    zIndex: 1,
    '& svg': {
        fontSize: 24,
        color: '#fff',
    },
}));

const TitleText = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    fontSize: '1.15rem',
    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.primary.main} 100%)`,
    backgroundSize: '200% auto',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: `${shimmer} 3s linear infinite`,
    position: 'relative',
    zIndex: 1,
    ...theme.applyStyles('dark', {
        backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light} 100%)`,
    }),
}));

const HeaderSubtext = styled(Typography)(({ theme }) => ({
    color: theme.vars.palette.text.secondary,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.25),
    position: 'relative',
    zIndex: 1,
}));

const HeaderIconButton = styled(IconButton)(({ theme }) => ({
    position: 'relative',
    zIndex: 1,
    color: alpha(theme.palette.primary.main, 0.8),
    transition: 'transform 0.2s ease, background-color 0.2s ease',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        transform: 'rotate(-15deg)',
    },
    ...theme.applyStyles('dark', {
        color: theme.palette.primary.light,
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.light, 0.15),
        },
    }),
}));

const StyledChatTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(2),
        transition: 'all 0.2s ease',
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(theme.palette.primary.main, 0.5),
        },
        '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
            },
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
        },
    },
    ...theme.applyStyles('dark', {
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.light,
            },
            '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.light,
                },
                boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
            },
        },
    }),
}));

const SendButton = styled(IconButton)(({ theme }) => ({
    width: 44,
    height: 44,
    borderRadius: theme.spacing(2),
    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: '#fff',
    boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    flexShrink: 0,
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
        backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    },
    '&.Mui-disabled': {
        backgroundImage: 'none',
        backgroundColor: alpha(theme.palette.text.disabled, 0.2),
        color: theme.palette.text.disabled,
        boxShadow: 'none',
    },
}));

const FloatingBotIcon = styled(SmartToyOutlined)(({ theme }) => ({
    fontSize: 56,
    color: alpha(theme.palette.primary.main, 0.4),
    animation: `${pulse} 3s ease-in-out infinite`,
    ...theme.applyStyles('dark', {
        color: alpha(theme.palette.primary.light, 0.5),
    }),
}));

const AIChatPanel: React.FC<AIChatPanelProps> = ({ open, onClose }) => {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();
    // Drawer slides in from the side opposite the user's reading direction so it never covers
    // the start of an interface element. RTL → left, LTR → right.
    const drawerAnchor: 'left' | 'right' = i18n.language === LanguageCode.AR ? 'left' : 'right';

    const [input, setInput] = React.useState('');
    const [displayMessages, setDisplayMessages] = React.useState<DisplayMessage[]>([]);
    const [apiHistory, setApiHistory] = React.useState<ChatTurnDto[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [tools, setTools] = React.useState<AvailableToolResponseDto[]>([]);
    const scrollRef = React.useRef<HTMLDivElement | null>(null);
    const toolsFetchedRef = React.useRef(false);

    const userInitial = (user?.name || user?.email || '?').trim().charAt(0).toUpperCase();

    React.useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }, [displayMessages, isLoading]);

    // Backs the empty-state "what can I help with" list with the caller's own real,
    // permission-filtered tool set instead of the two hardcoded example prompts alone — fetched
    // once per mount (this panel stays mounted for the whole session, Drawer visibility just
    // toggles via `open`) and only once the user actually opens it, not on every app load.
    React.useEffect(() => {
        if (!open || toolsFetchedRef.current) return;
        toolsFetchedRef.current = true;

        AI_SERVICE.getAvailableToolsAsync()
            .then((response) => {
                if (response.success && response.data) {
                    setTools(response.data);
                }
            })
            .catch(() => {
                // Silent — the static emptyHint text already covers this case.
            });
    }, [open]);

    const reset = () => {
        setDisplayMessages([]);
        setApiHistory([]);
        setError(null);
    };

    const handleSend = async () => {
        const message = input.trim();
        if (!message || isLoading) return;

        const nextId = Date.now();
        setDisplayMessages((prev) => [
            ...prev,
            {
                id: nextId,
                role: 'user',
                text: message,
            },
            {
                id: nextId + 1,
                role: 'assistant',
                text: '',
                isStreaming: true,
            },
        ]);
        setInput('');
        setIsLoading(true);
        setError(null);

        try {
            const response = await AI_SERVICE.chatAsync({
                userMessage: message,
                history: apiHistory.length > 0 ? apiHistory : null,
            });

            if (!response.success || !response.data) {
                setError(response.message || t('ai.chat.errors.unknown'));
                setDisplayMessages((prev) => prev.slice(0, -1));
                return;
            }

            const data = response.data;

            setDisplayMessages((prev) => {
                const next = [...prev];
                const lastIdx = next.findIndex((m) => m.id === nextId + 1);
                if (lastIdx >= 0) {
                    next[lastIdx] = {
                        id: nextId + 1,
                        role: 'assistant',
                        text: data.assistantText,
                    };
                }
                return next;
            });

            // Build the next-turn history in the same shape as the server expects. We don't
            // try to reconstruct the inner tool_use/tool_result turns the agent produced —
            // we just record the round-trip user/assistant pair, which is enough for the
            // model to see prior turns. The server will re-execute tools on the next turn
            // if needed.
            setApiHistory((prev) => [
                ...prev,
                {
                    role: 'user',
                    text: message,
                },
                {
                    role: 'assistant',
                    text: data.assistantText,
                },
            ]);
        } catch {
            // Anything caught here is a network/HTTP-layer failure (e.g. the raw
            // "HTTP error! Status: 500, Message: ..." thrown by http-client-wrapper.ts) —
            // never surface that technical detail to the user, always show the friendly
            // fallback instead.
            setError(t('ai.chat.errors.unknown'));
            setDisplayMessages((prev) => prev.slice(0, -1));
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <Drawer
            anchor={drawerAnchor}
            open={open}
            onClose={onClose}
            // Lift above the dashboard AppBar (default zIndex 1100) so the drawer's
            // own header isn't covered by the toolbar.
            sx={(theme) => ({ zIndex: theme.zIndex.modal })}
            PaperProps={{
                sx: {
                    width: PANEL_WIDTH,
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    border: 'none',
                },
            }}
        >
            <StyledDrawerPaper>
                <PanelHeader>
                    <GlowOrb />
                    <IconWrapper>
                        <SmartToyOutlined />
                    </IconWrapper>
                    <Box sx={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>
                        <TitleText>{t('ai.chat.title')}</TitleText>
                        <HeaderSubtext>{t('ai.chat.subtitle')}</HeaderSubtext>
                    </Box>
                    <Tooltip title={t('ai.chat.reset')}>
                        <span>
                            <HeaderIconButton size="small" onClick={reset} disabled={isLoading}>
                                <RestartAlt fontSize="small" />
                            </HeaderIconButton>
                        </span>
                    </Tooltip>
                    <HeaderIconButton size="small" onClick={onClose}>
                        <Close fontSize="small" />
                    </HeaderIconButton>
                </PanelHeader>

                <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', p: 2, minHeight: 0 }}>
                    {displayMessages.length === 0 && !isLoading && (
                        <Stack
                            spacing={1.5}
                            sx={{ mt: 6, alignItems: 'center', textAlign: 'center', px: 2 }}
                        >
                            <FloatingBotIcon />
                            <Typography
                                variant="body1"
                                fontWeight={600}
                                sx={{ color: 'primary.main' }}
                            >
                                {t('ai.chat.emptyState')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {t('ai.chat.emptyHint')}
                            </Typography>

                            {tools.length > 0 && (
                                <Box sx={{ width: '100%', mt: 1 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'text.secondary' }}
                                    >
                                        {t('ai.chat.capabilitiesTitle')}
                                    </Typography>
                                    <Stack direction="row" flexWrap="wrap" gap={0.75} justifyContent="center">
                                        {tools.map((tool) => (
                                            <Tooltip key={tool.name} title={tool.description} arrow>
                                                <Chip
                                                    label={truncateForChip(tool.description)}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={(theme) => ({
                                                        maxWidth: 220,
                                                        borderColor: alpha(theme.palette.primary.main, 0.3),
                                                        color: 'text.secondary',
                                                    })}
                                                />
                                            </Tooltip>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Stack>
                    )}

                    {displayMessages.map((m) => (
                        <AIChatMessage key={m.id} message={m} userInitial={userInitial} />
                    ))}

                    {error && (
                        <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    )}
                </Box>

                <Box
                    sx={(theme) => ({
                        p: 2,
                        borderTop: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                        backgroundImage: `linear-gradient(180deg, transparent 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
                    })}
                >
                    <Stack direction="row" spacing={1} alignItems="flex-end">
                        <StyledChatTextField
                            fullWidth
                            size="small"
                            multiline
                            maxRows={5}
                            placeholder={t('ai.chat.inputPlaceholder')}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isLoading}
                        />
                        <SendButton
                            onClick={handleSend}
                            disabled={isLoading || input.trim().length === 0}
                        >
                            <Send fontSize="small" />
                        </SendButton>
                    </Stack>
                </Box>
            </StyledDrawerPaper>
        </Drawer>
    );
};

export default AIChatPanel;
